"use client";

import { PinToHomeButton } from "@/components/home/shortcuts/PinToHomeButton";
import { ReadMoreHtml } from "@/components/read-more/ReadMoreHtml";
import {
  getRelativePosition,
  useGetArtworkById,
  useGetRelatedFieldsWithAttachments,
} from "@/api/artwork/artwork";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
} from "@/api/attachment";
import {
  useGetSculptureEditionForPreview,
  useGetSculptureEditionRelatedFields,
  useGetSculptureEditions,
} from "@/api/sculpture-edition/sculpture-edition";
import ArtworkPrintSingleDrawer from "@/components/artwork/ArtworkPrintSingleDrawer";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { RelativePositionNav } from "@/components/navigation/RelativePositionNav";
import { Button } from "@/components/ui/button";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useAuth } from "@/context/auth/AuthContext";
import { useArtworkPageData } from "@/hooks/useArtworkPageData";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useScrollToTopOnNavigate } from "@/hooks/useScrollToTopOnNavigate";
import {
  clearTranslationLocalStorageCache,
  getMainLanguage,
  I18N_DEFAULT_NS,
  isSupportedLocale,
  SupportedLocale,
} from "@/i18n";
import {
  PHOTOGRAPHY_TYPE_VALUES,
  SCULPTURE_TYPE_VALUES,
} from "@/lib/compute-field/artwork-module-helpers";
import { Artwork, PublicationStatus } from "@/lib/types";
import {
  getOeuvresDescriptionSecondaryTitleLines,
  resolveOeuvresDescriptionDisplayTitle,
} from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArtworkHeader } from "./ArtworkHeader";
import { ArtworkImages } from "./ArtworkImages";
import { ArtworkOverview } from "./ArtworkOverview";
import { ArtworkPhotoMasterInfo } from "./ArtworkPhotoMasterInfo";
import { ArtworkSculptureInfo } from "./ArtworkSculptureInfo";
import { ArtworkTirageInfo } from "./ArtworkTirageInfo";
import { ArtworkAccordions } from "./accordions";

export interface ArtworkPageProps {
  /**
   * Id de l'œuvre à afficher. Par défaut celui de la route
   * (`/edit/artwork/overview/:id`). Fourni explicitement quand la fiche est
   * montée hors route — aperçu du bloc Puck « Fiche (catalogue raisonné) ».
   */
  artworkId?: string;
  /**
   * Fiche montée dans une autre page (canvas de l'éditeur CMS) : on retire le
   * bandeau fixe du dashboard (navigation précédent/suivant, sélecteur de
   * langue, bouton Fermer) et la hauteur plein écran. Le corps de la fiche est
   * strictement identique — c'est ce qui garantit la parité avec le front.
   */
  embedded?: boolean;
}

/**
 * Bandeau fixe du dashboard (précédent/suivant, langue, Fermer).
 *
 * Isolé dans son propre composant parce qu'il est le SEUL consommateur de
 * `useDashboardParams` — lequel appelle `usePagination`, qui *throw* hors de
 * `DashboardParamsProviders` (monté uniquement sous les routes `/edit`). Monter
 * ce hook depuis la fiche elle-même ferait planter tout contexte qui l'affiche
 * ailleurs, à commencer par le canvas de l'éditeur CMS avancé.
 */
function ArtworkOverviewTopBar({
  id,
  title,
  artwork,
  localLocale,
  onLocaleChange,
}: {
  id?: string;
  title: string;
  artwork?: Artwork;
  localLocale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => void;
}) {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const navigate = useNavigate();
  const location = useLocation();
  const { relativePosition } = useDashboardParams<Artwork>({
    statusField: "oeuvresPublicationStatus",
    resourceName: "artwork",
    relativePosition: id
      ? { currentId: id, positionFetcher: getRelativePosition }
      : undefined,
  });

  return (
    <div className="grid grid-cols-[1fr_5fr_2fr] items-center w-full h-[70px] bg-[#FCFCFD] border-y px-5 fixed top-0 left-0 right-0 z-40 gap-4">
      <p className="text-sm font-bold uppercase leading-tight text-[#006EAB] xs:hidden md:block">
        {vt("bo.preview.artworkTitle", "Prévisualisation fiche œuvre")}
      </p>
      <RelativePositionNav
        relativePosition={relativePosition}
        buildLink={(navId) => `/edit/artwork/overview/${navId}${location.search}`}
        resourceType="artwork"
      >
        <p className="font-semibold text-lg truncate w-fit xs:hidden md:block">
          {title}
        </p>
      </RelativePositionNav>
      <div className="flex items-center gap-3 justify-end">
        {id && (
          <PinToHomeButton
            key={id}
            type="artwork"
            id={id}
            title={title ?? ""}
          />
        )}
        {artwork && <ArtworkPrintSingleDrawer artwork={artwork} labeledTrigger />}
        <MultilingualSelect
          value={localLocale}
          onValueChange={(value) => onLocaleChange(value as SupportedLocale)}
        />
        <Button
          onClick={() => id && navigate(`/edit/artwork/${id}`)}
          variant="ghost"
          className="text-sm font-semibold text-gray-950 cursor-pointer border"
        >
          {t("Close")}
        </Button>
      </div>
    </div>
  );
}

export function ArtworkPage({ artworkId, embedded = false }: ArtworkPageProps = {}) {
  const { i18n, t } = useTranslation();
  const { instanceData } = useAuth();
  const [localLocale, setLocalLocale] = useState<SupportedLocale>(
    i18n.language as SupportedLocale,
  );
  const [translationsLoaded, setTranslationsLoaded] = useState(0);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  const { id: routeId } = useParams();
  const location = useLocation();
  const id = artworkId ?? routeId;

  // Toujours ouvrir la prévisu en haut (le scroll du formulaire d'édition
  // est sinon conservé par la navigation SPA).
  useScrollToTopOnNavigate(
    id ? `${id}${location.search}` : undefined,
    !embedded,
  );

  const { data: artwork, isLoading: isLoadingArtwork } = useGetArtworkById(
    id,
    true,
    true,
  );
  const { data: relatedFields } = useGetRelatedFieldsWithAttachments(id);
  const { data: unfiltetredAttachments } = useGetAttachmentsByDocId(id);

  // Recharger les traductions depuis l'API quand la langue locale change.
  //
  // JAMAIS en mode `embedded` : ce rechargement est GLOBAL (il vide le cache
  // localStorage puis appelle `i18n.reloadResources`, qui émet `loaded`).
  // Dans le canvas de l'éditeur CMS avancé, `useTranslationBundleRevision`
  // écoute cet événement et son compteur entre dans la `key` de <Puck>
  // (cms/advanced/editor.tsx) : Puck se remonte, le canvas aussi, la fiche
  // remonte, elle recharge — boucle de re-render infinie.
  //
  // Hors dashboard il n'y a de toute façon pas de sélecteur de langue :
  // `localLocale` ne bouge jamais, et `getTranslation` retombe sur le bundle
  // déjà chargé par l'application.
  useEffect(() => {
    if (embedded) return;

    // Ne pas afficher le loader au premier chargement
    const isInitialLoad = translationsLoaded === 0;

    if (!isInitialLoad) {
      setIsLoadingTranslations(true);
    }

    clearTranslationLocalStorageCache([localLocale]);
    // Recharger les ressources i18n pour la nouvelle langue depuis l'API
    i18n
      .reloadResources(localLocale, I18N_DEFAULT_NS)
      .then(() => {
        // Forcer le re-render en mettant à jour l'état après le chargement
        setTranslationsLoaded((prev) => prev + 1);
        setIsLoadingTranslations(false);
      })
      .catch((error) => {
        console.warn("Failed to reload translations:", error);
        setIsLoadingTranslations(false);
      });
    // translationsLoaded est utilisé uniquement pour détecter le premier chargement, ne pas l'inclure dans les dépendances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLocale, i18n, embedded]);

  // Fonction pour obtenir une traduction dans une langue spécifique sans modifier la langue de l'interface
  const getTranslation = useMemo(() => {
    const loadResourceBundle = () => {
      // Essayer d'abord depuis i18n (qui devrait avoir les ressources après reloadResources)
      let resourceBundle = i18n.getResourceBundle(localLocale, "translation");
      if (resourceBundle) return resourceBundle;

      // Si pas trouvé dans i18n, charger depuis localStorage
      try {
        // i18next-localstorage-backend utilise le format: i18next_res_{lang}-{namespace}
        const possibleKeys = [
          `i18next_res_${localLocale}-translation`, // Format avec tiret (le bon format)
        ];

        for (const storageKey of possibleKeys) {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);

              // Le format peut être { translation: {...} } ou directement {...}
              if (parsed) {
                resourceBundle = parsed.translation || parsed;
                if (resourceBundle && typeof resourceBundle === "object") {
                  // Ajouter les ressources à i18n pour les prochaines fois
                  i18n.addResourceBundle(
                    localLocale,
                    "translation",
                    resourceBundle,
                    true,
                    true,
                  );
                  return resourceBundle;
                }
              }
            } catch (_parseError) {
              // Continuer avec la clé suivante
              continue;
            }
          }
        }
      } catch (e) {
        console.warn("Could not load translations from localStorage:", e);
      }

      return resourceBundle;
    };

    return (key: string, options?: any): string => {
      const resourceBundle = loadResourceBundle();

      // Si on a trouvé la ressource, utiliser la traduction
      if (resourceBundle && resourceBundle[key]) {
        let translation = resourceBundle[key];

        // Gérer l'interpolation si des options sont fournies
        if (options && typeof translation === "string") {
          Object.keys(options).forEach((optKey) => {
            if (optKey !== "lng" && optKey !== "count") {
              translation = translation.replace(
                new RegExp(`{{${optKey}}}`, "g"),
                String(options[optKey]),
              );
            }
          });
        }

        return typeof translation === "string" ? translation : key;
      }

      // Fallback: utiliser la fonction de traduction fixée
      try {
        const fixedT = i18n.getFixedT(localLocale, "translation");
        const result = fixedT(key, options);
        return typeof result === "string" ? result : key;
      } catch {
        // Dernier fallback: retourner la clé
        return key;
      }
    };
    // translationsLoaded est nécessaire pour forcer le recalcul quand les traductions sont rechargées
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, localLocale, translationsLoaded]);

  const attachments =
    artwork &&
    unfiltetredAttachments?.filter((a) =>
      shouldIncludeAttachment(
        a,
        artwork.oeuvresPublicationStatus as PublicationStatus,
      ),
    );

  // ── Sculpture detection ──────────────────────────────────────────────────
  // Mirrors the front-public flow:
  //  - Draft sculpture artwork + published SculptureEdition  ->  itemType = SCULPTURE
  //    (first published edition is displayed as the main entity, master is only the title context)
  //  - Published sculpture artwork                          ->  itemType = ARTWORK
  //    with the list of published editions rendered in the "Editions" drawer accordion
  const isSculptureTypeArtwork = useMemo(() => {
    if (!artwork || !instanceData?.isModuleSculptureEnabled) return false;
    const typeMultilingual = artwork.oeuvresDescriptionTypeOeuvreMultilingual;
    if (!typeMultilingual) return false;
    return Object.values(typeMultilingual).some((value) =>
      SCULPTURE_TYPE_VALUES.includes(
        value as (typeof SCULPTURE_TYPE_VALUES)[number],
      ),
    );
  }, [artwork, instanceData?.isModuleSculptureEnabled]);

  // Fetch published editions for this artwork (whether it's master or a draft-with-editions)
  const { data: fetchedSculptureEditions } = useGetSculptureEditions(
    isSculptureTypeArtwork && artwork ? artwork.id : "",
    {
      order: [{ createdAt: "asc" }],
      take: 100,
      skip: 0,
      where: { sculptureEditionPublicationStatus: "published" },
    },
  );

  // When the artwork is a draft sculpture + has at least one published edition,
  // we treat the overview as if we were looking at the first published edition.
  const firstPublishedEditionId =
    isSculptureTypeArtwork &&
    artwork?.oeuvresPublicationStatus === "draft" &&
    fetchedSculptureEditions &&
    fetchedSculptureEditions.length > 0
      ? fetchedSculptureEditions[0].id
      : undefined;

  const { data: fullSculptureEditionPreview } =
    useGetSculptureEditionForPreview(firstPublishedEditionId);

  // Determine item type and prepare the data payload fed to useArtworkPageData.
  const { itemType, artworkWithItemData } = useMemo(() => {
    if (!artwork)
      return { itemType: "ARTWORK" as const, artworkWithItemData: artwork };

    // If artwork has tirageData, it's a TIRAGE
    if ((artwork as any).tirageData) {
      return { itemType: "TIRAGE" as const, artworkWithItemData: artwork };
    }

    // Photography detection
    const typeMultilingual = artwork.oeuvresDescriptionTypeOeuvreMultilingual;
    const isPhotography = typeMultilingual
      ? Object.values(typeMultilingual).some((value) =>
          PHOTOGRAPHY_TYPE_VALUES.includes(
            value as (typeof PHOTOGRAPHY_TYPE_VALUES)[number],
          ),
        )
      : false;

    if (isPhotography) {
      const isDraft = artwork.oeuvresPublicationStatus === "draft";
      const publishedTirages =
        (artwork as any).Tirage && Array.isArray((artwork as any).Tirage)
          ? (artwork as any).Tirage.filter(
              (t: any) => t.tiragePublicationStatus === "published",
            )
          : [];

      if (isDraft && publishedTirages.length > 0) {
        const firstTirage = publishedTirages[0];
        return {
          itemType: "TIRAGE" as const,
          artworkWithItemData: { ...artwork, tirageData: firstTirage } as any,
        };
      }

      if (!isDraft) {
        return {
          itemType: "PHOTO_MASTER" as const,
          artworkWithItemData: artwork,
        };
      }
    }

    // Sculpture detection (mirrors TIRAGE):
    //  - draft master + published edition available -> SCULPTURE itemType
    if (isSculptureTypeArtwork) {
      const isDraft = artwork.oeuvresPublicationStatus === "draft";
      if (
        isDraft &&
        firstPublishedEditionId &&
        fullSculptureEditionPreview &&
        fullSculptureEditionPreview.id === firstPublishedEditionId
      ) {
        return {
          itemType: "SCULPTURE" as const,
          artworkWithItemData: {
            ...artwork,
            sculptureEditionData: fullSculptureEditionPreview,
          } as any,
        };
      }
      // published master -> ARTWORK but attach editions for the drawer accordion
      return {
        itemType: "ARTWORK" as const,
        artworkWithItemData: {
          ...artwork,
          sculptureEditions: fetchedSculptureEditions ?? [],
        } as any,
      };
    }

    return { itemType: "ARTWORK" as const, artworkWithItemData: artwork };
  }, [
    artwork,
    isSculptureTypeArtwork,
    firstPublishedEditionId,
    fullSculptureEditionPreview,
    fetchedSculptureEditions,
  ]);

  // When viewing a SCULPTURE edition (draft master + published edition), the
  // preview must show the edition's own images — not the master artwork's.
  const previewAttachments = useMemo(() => {
    if (itemType === "SCULPTURE" && fullSculptureEditionPreview?.attachments) {
      return fullSculptureEditionPreview.attachments.filter((a: any) =>
        shouldIncludeAttachment(
          a,
          (fullSculptureEditionPreview.sculptureEditionPublicationStatus ??
            "published") as PublicationStatus,
        ),
      );
    }
    return attachments || [];
  }, [itemType, fullSculptureEditionPreview, attachments]);

  // Data preparation using hook - must be called before conditional return
  const {
    displayData,
    mainImage,
    otherImages,
    isTirage,
    isPhotoMaster,
    isSculptureEdition,
    isSculptureMaster,
    tirageData,
    sculptureEditionData,
    sculptureEditions,
    masterInfo,
    computedFields,
    isPhotoModuleEnabledValue,
    provenanceMode,
  } = useArtworkPageData({
    data: (artworkWithItemData || ({} as any)) as any,
    attachments: previewAttachments,
    instanceData: instanceData ?? null,
    locale: localLocale,
    t: getTranslation,
    filterFromGeneralCR: { showPublicAuctions: true }, // TODO: Get from settings if available
    itemType,
  });

  // Fetch the edition's own related works when previewing a SCULPTURE edition.
  const { data: sculptureEditionRelated } = useGetSculptureEditionRelatedFields(
    itemType === "SCULPTURE" ? firstPublishedEditionId : undefined,
  );

  // When viewing a SCULPTURE edition, relations are scoped to the edition, not the master.
  const effectiveRelatedFields =
    itemType === "SCULPTURE"
      ? sculptureEditionRelated || {}
      : relatedFields || {};

  // Relation check - must be called before conditional return
  const relationNotEmpty = useMemo(() => {
    if (!effectiveRelatedFields) return false;
    return (
      (effectiveRelatedFields.relatedArtwork?.length ?? 0) > 0 ||
      (effectiveRelatedFields.from_Artwork_relatedArtwork?.length ?? 0) > 0 ||
      (effectiveRelatedFields.relatedAudiovisual?.length ?? 0) > 0 ||
      (effectiveRelatedFields.relatedDocument?.length ?? 0) > 0 ||
      (effectiveRelatedFields.relatedMultipleprint?.length ?? 0) > 0 ||
      (effectiveRelatedFields.relatedPhotography?.length ?? 0) > 0 ||
      (effectiveRelatedFields.relatedWriting?.length ?? 0) > 0
    );
  }, [effectiveRelatedFields]);

  const defaultLanguage = useMemo<SupportedLocale>(() => {
    const mainLanguage = getMainLanguage();
    return isSupportedLocale(mainLanguage) ? mainLanguage : "fr";
  }, []);

  const title = useMemo(() => {
    return resolveOeuvresDescriptionDisplayTitle(
      displayData,
      localLocale,
      defaultLanguage,
    );
  }, [defaultLanguage, displayData, localLocale]);

  const secondaryTitleLines = useMemo(() => {
    return getOeuvresDescriptionSecondaryTitleLines(
      displayData,
      localLocale,
      defaultLanguage,
      getTranslation,
    );
  }, [defaultLanguage, displayData, getTranslation, localLocale, translationsLoaded]);

  // Early return after all hooks have been called
  if (isLoadingArtwork) {
    return (
      <div
        className={
          embedded
            ? "w-full flex items-center justify-center py-20"
            : "w-full min-h-screen flex items-center justify-center"
        }
      >
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!artwork) {
    return <div>{t("artworkNexistePasLabel")}</div>;
  }

  return (
    <div className={embedded ? "w-full" : "w-full min-h-screen"}>
      {!embedded && (
        <ArtworkOverviewTopBar
          id={id}
          title={title}
          artwork={artwork}
          localLocale={localLocale}
          onLocaleChange={setLocalLocale}
        />
      )}
      <div className={embedded ? "w-full relative" : "w-full relative pt-[70px]"}>
        {isLoadingTranslations && (
          <div
            className={
              embedded
                ? "absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center"
                : "fixed inset-0 top-[70px] bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center"
            }
          >
            <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
          </div>
        )}
        <div className="w-full max-w-screen-2xl m-auto md:p-10 xs:p-5">
          <div className="flex xs:flex-col md:flex-row items-start justify-center text-center gap-10">
            <ArtworkImages
              mainImage={mainImage}
              otherImages={otherImages}
              locale={localLocale}
            />
            <div className="w-full h-full flex flex-col gap-5 text-left md:pr-16">
              <div className="flex items-center justify-between">
                <p className="font-normal text-base subpixel-antialiased">
                  {computedFields.artistsCalculated}
                </p>
              </div>
              <ArtworkHeader
                title={title || ""}
                secondaryTitleLines={secondaryTitleLines}
                registrationNumber={
                  itemType === "SCULPTURE" &&
                  masterInfo?.oeuvresDescriptionNumeroEnregistrementPublished !==
                    false
                    ? masterInfo?.oeuvresDescriptionNumeroEnregistrement || ""
                    : displayData?.oeuvresDescriptionNumeroEnregistrementPublished !==
                        false
                      ? displayData?.oeuvresDescriptionNumeroEnregistrement ||
                        ""
                      : ""
                }
                t={getTranslation}
              />
              <ArtworkOverview
                displayData={displayData}
                locale={localLocale}
                itemType={
                  isTirage
                    ? "TIRAGE"
                    : isSculptureEdition
                      ? "SCULPTURE"
                      : isPhotoMaster
                        ? "PHOTO_MASTER"
                        : "ARTWORK"
                }
                t={getTranslation}
                overviewSummaryCalculated={
                  computedFields.overviewSummaryCalculated
                }
                isPhotoModuleEnabled={isPhotoModuleEnabledValue}
              />
              {isTirage && tirageData && (
                <ArtworkTirageInfo
                  tirageData={tirageData}
                  locale={localLocale}
                  t={getTranslation}
                />
              )}
              {isTirage &&
                tirageData?.tirageRemarquesPubliquesPublished === true &&
                tirageData?.tirageRemarquesPubliquesMultilingual?.[
                  localLocale
                ] && (
                  <div className="my-1 mt-4 flex flex-col gap-4 font-medium text-sm subpixel-antialiased">
                    {getTranslation("singleArtworkSubtitlePublicNote", {
                      count: 0,
                    })}
                    <ReadMoreHtml
                      html={
                        tirageData.tirageRemarquesPubliquesMultilingual[
                          localLocale
                        ]
                      }
                      locale={localLocale}
                      className="font-normal text-sm subpixel-antialiased"
                    />
                  </div>
                )}
              {isSculptureEdition && sculptureEditionData && (
                <ArtworkSculptureInfo
                  sculptureEditionData={sculptureEditionData}
                  displayData={displayData}
                  locale={localLocale}
                  t={getTranslation}
                />
              )}
              {isSculptureEdition &&
                sculptureEditionData?.sculptureEditionRemarquesPubliquesPublished ===
                  true &&
                sculptureEditionData
                  ?.sculptureEditionRemarquesPubliquesMultilingual?.[
                  localLocale
                ] && (
                  <div className="my-1 mt-4 flex flex-col gap-4 font-medium text-sm subpixel-antialiased">
                    {getTranslation("singleArtworkSubtitlePublicNote", {
                      count: 0,
                    })}
                    <ReadMoreHtml
                      html={
                        sculptureEditionData
                          .sculptureEditionRemarquesPubliquesMultilingual[
                          localLocale
                        ]
                      }
                      locale={localLocale}
                      className="font-normal text-sm subpixel-antialiased"
                    />
                  </div>
                )}
              {isPhotoMaster && (
                <ArtworkPhotoMasterInfo
                  data={artwork}
                  displayData={displayData}
                  locale={localLocale}
                  t={getTranslation}
                />
              )}
              <ArtworkAccordions
                displayData={displayData}
                data={artwork}
                computedFields={computedFields}
                relatedFieldsWithAttachments={effectiveRelatedFields}
                relationNotEmpty={relationNotEmpty}
                isTirage={isTirage}
                isPhotoMaster={isPhotoMaster}
                isPhotoModuleEnabled={isPhotoModuleEnabledValue}
                isSculptureEdition={isSculptureEdition}
                isSculptureMaster={isSculptureMaster}
                sculptureEditions={sculptureEditions}
                locale={localLocale}
                t={getTranslation}
                provenanceMode={provenanceMode}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
